import { observer } from "mobx-react-lite";
import * as React from "react";
import { TasksGridVisualStore } from "../stores";
import { DatePicker, Form, Select } from "antd";
import { Utils } from "src/modules/common/misc/Utils";
import dayjs, { Dayjs } from "dayjs";
import AppModal from "../../common/components/AppModal";

type Props = {
    store: TasksGridVisualStore;
};

const FormItem = Form.Item;

const BulkTasksUpdateDialog: React.FC<Props> = ({ store }) => {
    const [form] = Form.useForm();

    React.useEffect(() => {
        const { selectedProject } = store;
        if (store.selectedProject) {
            store.loadUsersForProject(selectedProject);
        }
    }, []);

    const disabledDate = (current: Dayjs) => {
        return current && current < dayjs().endOf("day");
    };

    const handleSubmit = () => {
        form.validateFields().then((vals) => {
            const fields = {} as { [key: string]: unknown };
            Object.keys(vals).forEach((key) => {
                if (vals[key]) {
                    fields[key] =
                        key === "Reminder" || key === "DueDate"
                            ? Utils.formatDateAsUtcMidnightIso(vals[key])
                            : vals[key];
                }
            });
            store.updateFieldsBulk(fields);
        });
        store.setIsBulkUpdateDialogOpen(false);
    };

    return (
        <AppModal
            title={"Update fields"}
            open={store.isBulkUpdateDialogOpen}
            className="alpha-modal comment-in-modal"
            closable={false}
            cancelButtonTitle="Cancel"
            onCancelClick={() => store.setIsBulkUpdateDialogOpen(false)}
            cancelButtonProps={{
                size: undefined,
            }}
            submitButtonTitle="Save"
            submitButtonProps={{
                form: "bulk-update-form",
                size: undefined,
            }}
        >
            <Form
                form={form}
                onFinish={handleSubmit}
                layout="vertical"
                id="bulk-update-form"
            >
                <FormItem
                    name="AssignedTo"
                    colon={false}
                    className="dialog-field"
                    label={<span className="dialog-field-label">Assignee</span>}
                >
                    <Select
                        suffixIcon={
                            <i
                                className="alpha-icon xxs arrow-down-icon"
                                style={{ margin: 0, width: 8 }}
                            />
                        }
                        options={store.usersInProject
                            .filter((u) => !u.isDeleted)
                            .map((p) => ({
                                label: store.allUsersFullNameResolver[p.userId],
                                value: p.userId,
                            }))}
                        filterOption={Utils.filterOption}
                        showSearch
                        style={{ width: "100%" }}
                        loading={store.loadingUsers}
                        placeholder="Select assignee"
                    />
                </FormItem>
                <FormItem
                    name="DueDate"
                    colon={false}
                    className="dialog-field"
                    label={<span className="dialog-field-label">Due date</span>}
                >
                    <DatePicker
                        suffixIcon={
                            <i
                                className="alpha-icon xs calendar-icon"
                                style={{ margin: 0 }}
                            />
                        }
                        format={Utils.getDateFormat()}
                        style={{ width: "100%" }}
                        disabledDate={disabledDate}
                    />
                </FormItem>
                <FormItem
                    name="Reminder"
                    colon={false}
                    className="dialog-field"
                    label={<span className="dialog-field-label">Reminder</span>}
                >
                    <DatePicker
                        suffixIcon={
                            <i
                                className="alpha-icon xs calendar-icon"
                                style={{ margin: 0 }}
                            />
                        }
                        format={Utils.getDateFormat()}
                        style={{ width: "100%" }}
                        disabledDate={disabledDate}
                    />
                </FormItem>
                <FormItem
                    name="Priority"
                    colon={false}
                    className="dialog-field"
                    label={<span className="dialog-field-label">Priority</span>}
                >
                    <Select
                        suffixIcon={
                            <i
                                className="alpha-icon xxs arrow-down-icon"
                                style={{ margin: 0, width: 8 }}
                            />
                        }
                        style={{ width: "100%" }}
                        placeholder="Select priority"
                        options={Utils.getTaskPrioritiesForDropdown()}
                    />
                </FormItem>
                {store.selectedTasksShareSameStatus ? (
                    <FormItem
                        name="Status"
                        colon={false}
                        className="dialog-field"
                        label={
                            <span className="dialog-field-label">Status</span>
                        }
                    >
                        <Select
                            // TODO: Move suffixIcon styles to less file
                            suffixIcon={
                                <i
                                    className="alpha-icon xxs arrow-down-icon"
                                    style={{ margin: 0, width: 8 }}
                                />
                            }
                            options={store.taskStatusesForBulkUpdate.map(
                                (s) => ({ label: s.name, value: s.id }),
                            )}
                            style={{ width: "100%" }}
                            placeholder="Select status"
                        />
                    </FormItem>
                ) : null}
            </Form>
        </AppModal>
    );
};

export default observer(BulkTasksUpdateDialog);
